import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../component/Pagination";
import {
  fetchProducts,
  toggleProductStatus,
  deleteProduct,
  toggleProductPopular,
} from "../../../slice/product/productSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

/* ── Pick the first available image from colorVariants ── */
const getFirstImage = (product) => {
  if (!product.colorVariants?.length) return null;
  for (const variant of product.colorVariants) {
    const img =
      variant?.landscape?.images?.[0] ||
      variant?.portrait?.images?.[0];
    if (img) return img;
  }
  return null;
};

export default function ListProduct() {
  const navigate = useDispatch();
  const dispatch  = useDispatch();

  const [page,            setPage]            = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId,        setDeleteId]        = useState(null);

  const limit = 10;

  const {
    products   = [],
    loading,
    pagination = {},
  } = useSelector((state) => state.product);

  const nav = useNavigate();

  useEffect(() => {
    dispatch(fetchProducts({ page, limit }));
  }, [dispatch, page]);

  const handleDelete = () => {
    dispatch(deleteProduct(deleteId))
      .unwrap()
      .then(() => {
        toast.success("Product deleted successfully");
        setShowDeleteModal(false);
        setDeleteId(null);
        dispatch(fetchProducts({ page, limit }));
      })
      .catch(() => toast.error("Failed to delete product"));
  };

  return (
    <div className="row">
      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Product List</h4>
          <button
            className="btn cusBTNPop btn-sm"
            onClick={() => nav("/admin/product/add")}
          >
            + Add Product
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>MRP</th>
                <th>Selling</th>
                <th>Status</th>
                <th>Best Selling</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Loading...
                  </td>
                </tr>
              ) : products.length > 0 ? (
                products.map((product, index) => {
                  const firstImage = getFirstImage(product);
                  return (
                    <tr key={product._id}>
                      <td>{(page - 1) * limit + index + 1}</td>

                      {/* ── Image ── */}
                      <td>
                        {firstImage ? (
                          <img
                            src={`${process.env.REACT_APP_API_URL}/uploads/${firstImage}`}
                            alt={product.name}
                            width={50}
                            height={50}
                            style={{ objectFit: "cover", borderRadius: "6px", border: "1px solid #e5e7eb" }}
                          />
                        ) : (
                          <div style={{
                            width: 50, height: 50, borderRadius: "6px",
                            background: "#f3f4f6", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: 20, color: "#9ca3af",
                          }}>
                            🖼️
                          </div>
                        )}
                      </td>

                      <td>{product.name}</td>
                      <td>{product.category?.name || "—"}</td>
                      <td>₹{product.mrp}</td>
                      <td>₹{product.sellingPrice}</td>

                      {/* ── Status toggle ── */}
                      <td>
                        <div
                          className={`status-toggle ${product.status ? "enabled" : "disabled"}`}
                          onClick={() => dispatch(toggleProductStatus(product._id))}
                        >
                          <span className="toggle-knob" />
                          <span className="toggle-text">
                            {product.status ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                      </td>

                      {/* ── Best selling toggle ── */}
                      <td>
                        <div
                          className={`status-toggle ${product.bestSellingProduct ? "enabled" : "disabled"}`}
                          onClick={() => dispatch(toggleProductPopular(product._id))}
                        >
                          <span className="toggle-knob" />
                          <span className="toggle-text">
                            {product.bestSellingProduct ? "Yes" : "No"}
                          </span>
                        </div>
                      </td>

                      {/* ── Actions ── */}
                      <td>
                        <div className="d-flex gap-2">
                          {/* View */}
                          <button
                            className="btn btn-soft-primary btn-sm"
                            title="View"
                            onClick={() => nav(`/admin/products/view/${product._id}`)}
                          >
                            <iconify-icon icon="solar:eye-broken" className="align-middle fs-18" />
                          </button>

                          {/* Edit */}
                          <button
                            className="btn btn-soft-primary btn-sm"
                            title="Edit"
                            onClick={() => nav(`/admin/products/edit/${product._id}`)}
                          >
                            <iconify-icon icon="solar:pen-2-broken" />
                          </button>

                          {/* Delete */}
                          <button
                            className="btn btn-soft-danger btn-sm"
                            title="Delete"
                            onClick={() => {
                              setDeleteId(product._id);
                              setShowDeleteModal(true);
                            }}
                          >
                            <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="card-footer border-top">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* ── Delete confirmation modal ── */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Delete Product</h5>
              <button className="btn-close" onClick={() => setShowDeleteModal(false)} />
            </div>

            <div className="modal-body text-center py-3">
              Are you sure you want to delete this product?
            </div>

            <div className="modal-footer">
              <button className="btn cusClose" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn cuscancel" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}